import {
  Component,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Overlay } from '@angular/cdk/overlay';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { of, Subject, Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DataManagerService } from '../../../data/services/DataManager/data-manager.service';
import { MenuModel } from '../../../data/model/MenuModel/menu.model';
import { AppConstants } from '../../../shared/constants/app-constants';
import { clearListFilters, NAV_GROUP_FALLBACK, NAV_GROUPS, navGroupLabel } from '../../nav-drawer/nav-shared';
import { NAV_TABS, NavTabContext, routeRoot } from '../../nav-drawer/nav-tabs';
import { TopbarSearchResult } from './topbar-search.model';
import { looksLikeCardNumber } from './record-search/card-number-guard';
import { MIN_RECORD_QUERY_LENGTH, RecordSearchService } from './record-search/record-search.service';

const NAVIGATION = 'Navigation';
/** Wait for a pause in typing before hitting the APIs. */
const RECORD_SEARCH_DEBOUNCE_MS = 250;
/** Section order of the sidebar, so the empty-query list reads like the menu. */
const SECTION_ORDER = NAV_GROUPS.map(g => g.label).concat(NAV_GROUP_FALLBACK);

/**
 * Global search: the "Search  Ctrl K" pill in the topbar and the panel it opens.
 * The panel is a MatDialog (focus trap, Esc, backdrop and focus restore come with it).
 * Navigation results come from the user's real backend menu (dataManager.menuArray, restored
 * from localStorage 'user_menu' on reload), so they match the sidebar and its permissions.
 * Record results (transactions, disputes, customers…) come from RecordSearchService and are
 * listed under the navigation matches, one group per provider.
 * Reference: design/prototype/assets/shell.js (⌘K palette), components.css (.pa-cmdk*).
 */
@Component({
  selector: 'app-topbar-search',
  templateUrl: './topbar-search.component.html',
  styleUrls: ['./topbar-search.component.css'],
  standalone: false
})
export class TopbarSearchComponent implements OnInit, OnDestroy {
  @ViewChild('panelTpl') panelTpl: TemplateRef<unknown>;

  query = '';
  results: TopbarSearchResult[] = [];
  activeIndex = 0;
  /** True while record providers are being queried for the current text. */
  recordsLoading = false;
  /** '⌘K' on Apple platforms, 'Ctrl K' elsewhere. */
  shortcutHint = 'Ctrl K';

  private navIndex: TopbarSearchResult[] = [];
  private navMatches: TopbarSearchResult[] = [];
  private recordResults: TopbarSearchResult[] = [];
  /** Route roots of the user's menu pages and the tab/permission context, captured on open. */
  private menuRoots = new Set<string>();
  private tabCtx: NavTabContext;
  private readonly recordQuery$ = new Subject<string>();
  private recordSub?: Subscription;
  private dialogRef: MatDialogRef<unknown> | null = null;
  private readonly isBrowser: boolean;
  private removeHotkey?: () => void;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private overlay: Overlay,
    private zone: NgZone,
    private dataManager: DataManagerService,
    private recordSearch: RecordSearchService,
    @Inject('LOCALSTORAGE') private localStorage: any,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  get isOpen(): boolean {
    return !!this.dialogRef;
  }

  /** The panel refuses to search anything that looks like a PAN. */
  get blockedAsCardNumber(): boolean {
    return looksLikeCardNumber(this.query);
  }

  ngOnInit(): void {
    // switchMap comes first so every new text (or close, which sends '') cancels the in-flight
    // request at once; the debounce runs inside it. With the debounce outside, an old request
    // stayed subscribed for the debounce window and could repaint the panel with stale matches.
    this.recordSub = this.recordQuery$.pipe(
      switchMap(q => (q
        ? timer(RECORD_SEARCH_DEBOUNCE_MS).pipe(switchMap(() => (this.isOpen
          ? this.recordSearch.search(q, this.menuRoots, this.tabCtx)
          : of({ results: [], pending: false }))))
        : of({ results: [], pending: false })))
    ).subscribe(update => {
      // Each provider's group appears as soon as it answers; "Searching…" stays until the last one.
      this.recordsLoading = this.isOpen && update.pending;
      this.recordResults = this.isOpen ? update.results : [];
      this.refreshResults();
    });

    if (!this.isBrowser) {
      return;
    }
    if (/Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)) {
      this.shortcutHint = '⌘K';
    }
    // Keydown fires constantly; keep it out of change detection and re-enter only on the hotkey.
    const onKeydown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key?.toLowerCase() === 'k') {
        e.preventDefault();
        this.zone.run(() => (this.isOpen ? this.close() : this.open()));
      }
    };
    this.zone.runOutsideAngular(() => document.addEventListener('keydown', onKeydown));
    this.removeHotkey = () => document.removeEventListener('keydown', onKeydown);
  }

  ngOnDestroy(): void {
    this.removeHotkey?.();
    this.recordSub?.unsubscribe();
    this.close();
  }

  open(): void {
    if (this.dialogRef) {
      return;
    }
    // Rebuilt per open: the menu changes on login / login-as-merchant / account creation.
    this.tabCtx = this.tabContext();
    this.navIndex = this.buildNavIndex(this.dataManager.menuArray);
    this.navMatches = this.navIndex;
    this.recordResults = [];
    this.recordsLoading = false;
    this.refreshResults();
    this.activeIndex = 0;
    this.dialogRef = this.dialog.open(this.panelTpl, {
      ariaLabel: 'Search',
      autoFocus: 'first-tabbable',
      width: '620px',
      maxWidth: '94vw',
      position: { top: '12vh' },
      panelClass: 'pa-cmdk-dialog',
      backdropClass: 'pa-cmdk-backdrop',
      // The page scrolls inside .page-container, not the document; the default block strategy pins
      // <html> (position:fixed + .cdk-global-scrollblock) and shifts the layout under the blur.
      scrollStrategy: this.overlay.scrollStrategies.noop()
    });
    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = null;
      this.query = '';
      this.navMatches = [];
      this.recordResults = [];
      this.recordsLoading = false;
      this.results = [];
      this.activeIndex = 0;
      this.recordQuery$.next('');
    });
  }

  close(): void {
    this.dialogRef?.close();
  }

  onQueryChange(value: string): void {
    this.query = value;
    this.activeIndex = 0;
    const q = value.trim();
    // Never match, send, store or log a card-number-like query.
    const blocked = this.blockedAsCardNumber;
    this.navMatches = blocked ? [] : this.filterNav(q.toLowerCase());
    // Old records belong to the old text; drop them rather than show mismatches while loading.
    this.recordResults = [];
    this.recordsLoading = !blocked && q.length >= MIN_RECORD_QUERY_LENGTH;
    this.refreshResults();
    this.recordQuery$.next(this.recordsLoading ? q : '');
  }

  onPanelKeydown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.moveActive(-1);
        break;
      case 'Enter':
        if (this.results[this.activeIndex]) {
          e.preventDefault();
          this.select(this.results[this.activeIndex]);
        }
        break;
    }
  }

  select(result: TopbarSearchResult): void {
    // Same as a sidebar click: a list opened from the menu starts unfiltered. Opening a record
    // detail leaves the user's saved list filters alone.
    if (this.isBrowser && result.group === NAVIGATION) {
      clearListFilters(this.localStorage);
    }
    this.close();
    this.router.navigate(result.route, { queryParams: result.queryParams });
  }

  /** Navigation matches first, then record groups in provider order. */
  private refreshResults(): void {
    this.results = this.navMatches.concat(this.recordResults);
    if (this.activeIndex >= this.results.length) {
      this.activeIndex = 0;
    }
  }

  /** True when row i starts a new group (drives the group heading). */
  startsGroup(i: number): boolean {
    return i === 0 || this.results[i - 1].group !== this.results[i].group;
  }

  /**
   * One result per sidebar destination, routed exactly like NavDrawerComponent:
   * plain items → menu.routes; items with a submenu → menu.routerLink, plus each submenu entry
   * (hidden when the sidebar hides them, i.e. a first submenu entry of 'batch-detail').
   * Then the in-page tabs (NAV_TABS) of those pages, after every menu item: only for a parent page
   * that is in the user's menu, and only when the page itself would show the tab.
   */
  private buildNavIndex(menus: MenuModel[]): TopbarSearchResult[] {
    const out: { r: TopbarSearchResult; order: number }[] = [];
    const seen = new Set<string>();
    const add = (label: string, sublabel: string, order: number, path: string) => {
      const key = label + '|' + path;
      if (!label || !path || seen.has(key)) {
        return;
      }
      seen.add(key);
      out.push({ r: { group: NAVIGATION, label, sublabel, route: [path] }, order });
    };
    // Menu pages by route root ('payment', 'balance', …), for attaching their tabs.
    const pages = new Map<string, { name: string; section: string }>();

    (Array.isArray(menus) ? menus : []).forEach(menu => {
      if (!menu) {
        return;
      }
      const section = navGroupLabel(menu.name);
      const order = SECTION_ORDER.indexOf(section);
      // Mirrors the sidebar template: subMenu === null → plain link; an array → group; anything else isn't rendered.
      if (menu.subMenu === null) {
        add(menu.name, section, order, menu.routes);
      } else if (Array.isArray(menu.subMenu)) {
        add(menu.name, section, order, menu.routerLink || menu.routes);
        if (menu.subMenu[0]?.routerLink !== 'batch-detail') {
          menu.subMenu.forEach(sub => add(sub?.name, menu.name + ' · ' + section, order, sub?.routerLink));
        }
      } else {
        return;
      }
      [menu.routes, menu.routerLink].map(routeRoot).filter(Boolean)
        .forEach(root => pages.has(root) || pages.set(root, { name: menu.name, section }));
    });

    // Record providers use the same gate: only pages in the user's menu.
    this.menuRoots = new Set(pages.keys());
    const ctx = this.tabCtx;
    NAV_TABS.forEach(tab => {
      const page = pages.get(tab.parent);
      if (page && (!tab.visible || tab.visible(ctx))) {
        // Tabs sort after all menu items, still grouped by their page's section.
        add(tab.label, page.name + ' · ' + page.section, SECTION_ORDER.length + SECTION_ORDER.indexOf(page.section), tab.route);
      }
    });

    // Stable sort by sidebar section; keeps backend order within a section.
    return out
      .map((x, i) => ({ ...x, i }))
      .sort((a, b) => a.order - b.order || a.i - b.i)
      .map(x => x.r);
  }

  /** Same inputs the pages use for their tab conditions (see payment.component.ts ngOnInit). */
  private tabContext(): NavTabContext {
    let settings: Record<string, any>;
    try {
      settings = JSON.parse(this.localStorage.getItem(AppConstants.STORAGE_KEY_ACCOUNT_SETTINGS)) || {};
    } catch {
      settings = {};
    }
    let paypalAccount = false;
    try {
      const accounts: any[] = JSON.parse(this.localStorage.getItem(AppConstants.STORAGE_KEY_ACCOUNT_ID));
      const currentAccountId = JSON.parse(this.localStorage.getItem(AppConstants.STORAGE_KEY_CURRENT_ACCOUNT_ID));
      paypalAccount = (Array.isArray(accounts) ? accounts : []).find(a => a?.id === currentAccountId)?.account_type === 'paypal';
    } catch {
      paypalAccount = false;
    }
    const permissions: any[] = Array.isArray(this.dataManager.permissionsArray) ? this.dataManager.permissionsArray : [];
    return {
      settings,
      can: permission => permissions.some(p => p?.permission_name === permission),
      paypalAccount
    };
  }

  /** Label prefix > word start > anywhere in label > anywhere in sublabel; ties keep menu order. */
  private filterNav(q: string): TopbarSearchResult[] {
    if (!q) {
      return this.navIndex;
    }
    const score = (r: TopbarSearchResult): number => {
      const label = r.label.toLowerCase();
      if (label.startsWith(q)) { return 4; }
      if ((' ' + label).includes(' ' + q)) { return 3; }
      if (label.includes(q)) { return 2; }
      return (r.sublabel || '').toLowerCase().includes(q) ? 1 : 0;
    };
    return this.navIndex
      .map((r, i) => ({ r, i, s: score(r) }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s || a.i - b.i)
      .map(x => x.r);
  }

  private moveActive(step: number): void {
    const count = this.results.length;
    if (!count) {
      return;
    }
    this.activeIndex = (this.activeIndex + step + count) % count;
    document.getElementById('pa-cmdk-opt-' + this.activeIndex)?.scrollIntoView({ block: 'nearest' });
  }
}
